import { addSignalAttribute, identify, init, reportError } from '@dash0/sdk-web';
import { User } from '@nextroll/ar-base-api';

/**
 * Dash0 RUM (real user monitoring) initialization for rofl-ui.
 *
 * Environments are distinguished by the `deployment.environment.name` resource
 * attribute (derived from `environment` below), so all environments report under
 * a single service name rather than splitting the dashboards.
 */

// [0, 100] Percentage of sessions to record, per environment.
const SESSION_SAMPLING_RATE_BY_ENV: Record<string, number> = {
    development: 0,
    staging: 10,
    production: 50,
};

let initialized = false;

export function initDash0(): void {
    const endpoint = process.env.DASH0_ENDPOINT_URL;
    const authToken = process.env.DASH0_AUTH_TOKEN;

    if (!endpoint || !authToken) {
        return;
    }

    const environment = process.env.ENVIRONMENT;
    const release = process.env.VERSION;

    init({
        serviceName: 'rofl-ui',
        environment,
        serviceVersion: release,
        deploymentName: 'main',
        deploymentId: release || 'local',
        endpoint: { url: endpoint, authToken },
        sessionSamplingRate: SESSION_SAMPLING_RATE_BY_ENV[environment ?? ''] ?? 0,
    });

    initialized = true;
}

/**
 * Attach the current user's identity to Dash0 signals. Mirrors the identity set
 * for telemetry in WorkflowsApp: `user` is the primary logged-in identity, and
 * `actualUser` (who differs from `user` during impersonation) is attached as
 * supplementary attributes.
 */
export function identifyDash0User(user?: User, actualUser?: User): void {
    if (!initialized || !user) {
        return;
    }

    identify(user.eid, {
        email: user.email,
        roles: user.role ? [user.role] : undefined,
    });

    if (actualUser && actualUser.eid !== user.eid) {
        addSignalAttribute('actual_user.id', actualUser.eid);
        addSignalAttribute('actual_user.email', actualUser.email);
        if (actualUser.role) {
            addSignalAttribute('actual_user.role', actualUser.role);
        }
    }
}

/**
 * Forward an error to Dash0. No-op if Dash0 was never initialized. Wrapped so a
 * failure here can never break the surrounding error-handling path.
 */
export function reportDash0Error(error: unknown): void {
    if (!initialized) {
        return;
    }

    try {
        reportError(error instanceof Error ? error : new Error(String(error)));
    } catch (dash0Error) {
        // eslint-disable-next-line no-console
        console.error('Error reporting to Dash0:', dash0Error);
    }
}
